<script setup lang="ts" generic="TData, TValue">
import {
    FlexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getExpandedRowModel,
    useVueTable,
} from '@tanstack/vue-table';
import type {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
} from '@tanstack/vue-table';
import { useDebounceFn } from '@vueuse/core';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Search,
} from 'lucide-vue-next';
import { ref, watch } from 'vue';
import type { HTMLAttributes } from 'vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

interface Props {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    loading?: boolean;
    showSearch?: boolean;
    showSelectionCount?: boolean;
    searchPlaceholder?: string;
    class?: HTMLAttributes['class'];
    expandable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    loading: false,
    showSearch: true,
    showSelectionCount: false,
    searchPlaceholder: 'Rechercher...',
    expandable: false,
});

const sorting = ref<SortingState>([]);
const columnFilters = ref<ColumnFiltersState>([]);
const rowSelection = ref({});
const globalFilter = ref('');
const searchInput = ref('');

const debouncedSearch = useDebounceFn((value: string) => {
    globalFilter.value = value;
}, 300);

watch(searchInput, (newValue) => {
    debouncedSearch(newValue);
});

const table = useVueTable({
    get data() {
        return props.data;
    },
    get columns() {
        return props.columns;
    },
    state: {
        get sorting() {
            return sorting.value;
        },
        get columnFilters() {
            return columnFilters.value;
        },
        get rowSelection() {
            return rowSelection.value;
        },
        get globalFilter() {
            return globalFilter.value;
        },
    },
    enableMultiSort: true,
    onSortingChange: (updaterOrValue) => {
        sorting.value =
            typeof updaterOrValue === 'function'
                ? updaterOrValue(sorting.value)
                : updaterOrValue;
    },
    onColumnFiltersChange: (updaterOrValue) => {
        columnFilters.value =
            typeof updaterOrValue === 'function'
                ? updaterOrValue(columnFilters.value)
                : updaterOrValue;
    },
    onRowSelectionChange: (updaterOrValue) => {
        rowSelection.value =
            typeof updaterOrValue === 'function'
                ? updaterOrValue(rowSelection.value)
                : updaterOrValue;
    },
    onGlobalFilterChange: (updaterOrValue) => {
        globalFilter.value =
            typeof updaterOrValue === 'function'
                ? updaterOrValue(globalFilter.value)
                : updaterOrValue;
    },
    getRowCanExpand: () => props.expandable,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
});
</script>

<template>
    <div class="space-y-4 p-4">
        <div
            v-if="showSearch || $slots.actions"
            class="flex items-center justify-between gap-4"
        >
            <div v-if="showSearch" class="relative w-full max-w-sm">
                <Search
                    class="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground"
                />
                <Input
                    type="search"
                    :placeholder="searchPlaceholder"
                    class="pl-9"
                    v-model="searchInput"
                />
            </div>
            <div class="flex items-center gap-2">
                <slot name="actions" />
            </div>
        </div>

        <div :class="['rounded-md border', $props.class]">
            <Table>
                <TableHeader>
                    <TableRow
                        v-for="headerGroup in table.getHeaderGroups()"
                        :key="headerGroup.id"
                    >
                        <TableHead
                            v-for="header in headerGroup.headers"
                            :key="header.id"
                        >
                            <div
                                v-if="!header.isPlaceholder"
                                :class="[
                                    header.column.getCanSort()
                                        ? 'flex cursor-pointer items-center gap-2 select-none'
                                        : '',
                                ]"
                                @click="
                                    header.column.getToggleSortingHandler()?.(
                                        $event,
                                    )
                                "
                            >
                                <FlexRender
                                    :render="header.column.columnDef.header"
                                    :props="header.getContext()"
                                />
                                <template v-if="header.column.getCanSort()">
                                    <ArrowUpDown
                                        v-if="!header.column.getIsSorted()"
                                        class="h-4 w-4 text-muted-foreground"
                                    />
                                    <ArrowUp
                                        v-else-if="
                                            header.column.getIsSorted() ===
                                            'asc'
                                        "
                                        class="h-4 w-4"
                                    />
                                    <ArrowDown v-else class="h-4 w-4" />
                                </template>
                            </div>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <template v-if="loading">
                        <TableRow v-for="i in 5" :key="i">
                            <TableCell
                                v-for="header in table.getHeaderGroups()[0]
                                    .headers"
                                :key="header.id"
                            >
                                <Skeleton class="h-6 w-full" />
                            </TableCell>
                        </TableRow>
                    </template>
                    <template v-else-if="table.getRowModel().rows?.length">
                        <template
                            v-for="row in table.getRowModel().rows"
                            :key="row.id"
                        >
                            <TableRow
                                :data-state="row.getIsSelected() && 'selected'"
                                :class="
                                    expandable
                                        ? 'cursor-pointer hover:bg-neutral-50/50'
                                        : ''
                                "
                                @click="
                                    expandable ? row.toggleExpanded() : null
                                "
                            >
                                <TableCell
                                    v-for="cell in row.getVisibleCells()"
                                    :key="cell.id"
                                >
                                    <FlexRender
                                        :render="cell.column.columnDef.cell"
                                        :props="cell.getContext()"
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow v-if="expandable && row.getIsExpanded()">
                                <TableCell :colspan="columns.length">
                                    <slot name="row-expansion" :row="row" />
                                </TableCell>
                            </TableRow>
                        </template>
                    </template>
                    <TableRow v-else>
                        <TableCell
                            :colspan="columns.length"
                            class="h-24 text-center"
                        >
                            Aucun résultat.
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>

        <div class="flex items-center justify-between px-2">
            <div class="flex-1 text-sm text-muted-foreground">
                <template v-if="showSelectionCount">
                    {{ table.getFilteredSelectedRowModel().rows.length }} sur
                    {{ table.getFilteredRowModel().rows.length }} ligne(s)
                    sélectionnée(s).
                </template>
            </div>
            <div class="flex items-center space-x-6 lg:space-x-8">
                <div class="flex items-center space-x-2">
                    <p class="text-sm font-medium">Lignes par page</p>
                    <Select
                        :value="`${table.getState().pagination.pageSize}`"
                        @update:value="table.setPageSize(Number($event))"
                    >
                        <SelectTrigger class="h-8 w-17.5">
                            <SelectValue
                                :placeholder="`${table.getState().pagination.pageSize}`"
                            />
                        </SelectTrigger>
                        <SelectContent side="top">
                            <SelectItem
                                v-for="pageSize in [10, 20, 30, 40, 50]"
                                :key="pageSize"
                                :value="`${pageSize}`"
                            >
                                {{ pageSize }}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div
                    class="flex w-25 items-center justify-center text-sm font-medium"
                >
                    Page {{ table.getState().pagination.pageIndex + 1 }} sur
                    {{ table.getPageCount() }}
                </div>
                <div class="flex items-center space-x-2">
                    <Button
                        variant="outline"
                        class="hidden h-8 w-8 p-0 lg:flex"
                        :disabled="!table.getCanPreviousPage()"
                        @click="table.setPageIndex(0)"
                    >
                        <span class="sr-only">Première page</span>
                        <ChevronsLeft class="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        class="h-8 w-8 p-0"
                        :disabled="!table.getCanPreviousPage()"
                        @click="table.previousPage()"
                    >
                        <span class="sr-only">Page précédente</span>
                        <ChevronLeft class="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        class="h-8 w-8 p-0"
                        :disabled="!table.getCanNextPage()"
                        @click="table.nextPage()"
                    >
                        <span class="sr-only">Page suivante</span>
                        <ChevronRight class="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        class="hidden h-8 w-8 p-0 lg:flex"
                        :disabled="!table.getCanNextPage()"
                        @click="table.setPageIndex(table.getPageCount() - 1)"
                    >
                        <span class="sr-only">Dernière page</span>
                        <ChevronsRight class="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>
